import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  clearAuthToken,
  clearSupabaseSession,
  createBillingCheckout,
  createBillingPortal,
  getActiveAuthToken,
  getEmailSettings,
  getMe,
  getMyCredits,
  getMyEmailDeliveries,
  getMyPayments,
  getMyRuns,
  getSupabaseClient,
  hasSupabaseConfig,
  loadSupabaseSession,
  notifyAuthChange,
  setAuthToken,
  signOutSupabaseSession,
  updateEmailSettings,
} from '../services/api';

type AccountBundle = {
  me: Record<string, any> | null;
  credits: Record<string, any> | null;
  payments: Record<string, any> | null;
  emailSettings: Record<string, any> | null;
  runs: Record<string, any> | null;
  emailDeliveries: Record<string, any> | null;
};

type AuthMode = 'supabase' | 'token';

function formatNumber(value: unknown): string {
  const number = Number(value || 0);
  return Number.isFinite(number) ? new Intl.NumberFormat('zh-CN').format(number) : '0';
}

function formatTime(value: unknown): string {
  if (typeof value !== 'string' || !value.trim()) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return new Intl.DateTimeFormat('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
}

function shortToken(value: string): string {
  if (!value) return '未登录';
  if (value.length <= 18) return value;
  return `${value.slice(0, 10)}...${value.slice(-8)}`;
}

export default function AccountPanel() {
  const navigate = useNavigate();
  const [tokenInput, setTokenInput] = useState(() => getActiveAuthToken());
  const [authMode, setAuthMode] = useState<AuthMode>(() => (loadSupabaseSession() ? 'supabase' : 'token'));
  const [supabaseEmail, setSupabaseEmail] = useState('');
  const [supabasePassword, setSupabasePassword] = useState('');
  const [bundle, setBundle] = useState<AccountBundle>({ me: null, credits: null, payments: null, emailSettings: null, runs: null, emailDeliveries: null });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [checkoutCredits, setCheckoutCredits] = useState('100');
  const [emailDraft, setEmailDraft] = useState({ resultEmail: '', sendResultEmail: false, defaultArtifactTtlHours: 24 });

  const token = getActiveAuthToken();
  const user = (bundle.me?.user || {}) as Record<string, any>;
  const credits = (bundle.credits?.credits || {}) as Record<string, any>;
  const ledger = Array.isArray(bundle.credits?.ledger) ? bundle.credits?.ledger as Array<Record<string, any>> : [];
  const payments = Array.isArray(bundle.payments?.payments) ? bundle.payments?.payments as Array<Record<string, any>> : [];
  const runs = Array.isArray(bundle.runs?.runs) ? bundle.runs?.runs as Array<Record<string, any>> : [];
  const deliveries = Array.isArray(bundle.emailDeliveries?.deliveries) ? bundle.emailDeliveries?.deliveries as Array<Record<string, any>> : [];
  const supabaseConfigured = hasSupabaseConfig();
  const canLoadAccount = useMemo(() => Boolean(token.trim()), [token]);

  const loadAccount = useCallback(async () => {
    setLoading(true);
    setError('');
    setNotice('');
    try {
      const [me, creditsResult, paymentsResult, emailSettings, runsResult, emailDeliveries] = await Promise.all([
        getMe(),
        getMyCredits(20),
        getMyPayments(20),
        getEmailSettings(),
        getMyRuns(20),
        getMyEmailDeliveries(20),
      ]);
      setBundle({ me, credits: creditsResult, payments: paymentsResult, emailSettings, runs: runsResult, emailDeliveries });
      const settings = (emailSettings.settings || {}) as Record<string, any>;
      setEmailDraft({
        resultEmail: String(settings.resultEmail || ''),
        sendResultEmail: Boolean(settings.sendResultEmail),
        defaultArtifactTtlHours: Number(settings.defaultArtifactTtlHours || 24),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) void loadAccount();
  }, [loadAccount, token]);

  useEffect(() => {
    const onAuthChange = () => {
      setTokenInput(getActiveAuthToken());
      if (getActiveAuthToken()) void loadAccount();
    };
    window.addEventListener('academic-review-auth-change', onAuthChange);
    return () => window.removeEventListener('academic-review-auth-change', onAuthChange);
  }, [loadAccount]);

  const handleSaveToken = async () => {
    clearSupabaseSession();
    setAuthToken(tokenInput);
    notifyAuthChange();
    await loadAccount();
  };

  const handleClearToken = () => {
    clearAuthToken();
    clearSupabaseSession();
    setTokenInput('');
    setBundle({ me: null, credits: null, payments: null, emailSettings: null, runs: null, emailDeliveries: null });
    setNotice('本地会话已清除。');
    setError('');
    notifyAuthChange();
  };

  const handleSaveEmail = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await updateEmailSettings(emailDraft);
      setBundle((current) => ({ ...current, emailSettings: result }));
      setNotice('邮件偏好已保存。');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleCheckout = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const creditsValue = Number.parseInt(checkoutCredits, 10);
      const result = await createBillingCheckout({
        credits: Number.isFinite(creditsValue) && creditsValue > 0 ? creditsValue : undefined,
        idempotencyKey: `checkout-${Date.now()}`,
      });
      const url = typeof result.url === 'string' ? result.url : '';
      if (url) {
        window.location.href = url;
        return;
      }
      setNotice('Checkout 已创建，但 Stripe 未返回跳转地址。');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleBillingPortal = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await createBillingPortal({
        returnUrl: window.location.href,
        idempotencyKey: `portal-${Date.now()}`,
      });
      const url = typeof result.url === 'string' ? result.url : '';
      if (url) {
        window.location.href = url;
        return;
      }
      setNotice('Billing portal 已创建，但 Stripe 未返回跳转地址。');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleSupabaseSignIn = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const client = await getSupabaseClient();
      if (!client) throw new Error('Supabase URL 或 anon key 未配置');
      const { data, error: signInError } = await client.auth.signInWithPassword({ email: supabaseEmail.trim(), password: supabasePassword });
      if (signInError) throw signInError;
      const accessToken = data.session?.access_token || '';
      if (!accessToken) throw new Error('Supabase 未返回 access token');
      setTokenInput(accessToken);
      notifyAuthChange();
      setNotice('Supabase 登录成功。');
      await loadAccount();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleSupabaseSignUp = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const client = await getSupabaseClient();
      if (!client) throw new Error('Supabase URL 或 anon key 未配置');
      const { data, error: signUpError } = await client.auth.signUp({ email: supabaseEmail.trim(), password: supabasePassword });
      if (signUpError) throw signUpError;
      const accessToken = data.session?.access_token || '';
      if (accessToken) {
        setTokenInput(accessToken);
        notifyAuthChange();
        setNotice('Supabase 注册成功并已登录。');
        await loadAccount();
        return;
      }
      setNotice('注册已提交，请完成邮箱验证后登录。');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleSupabaseSignOut = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await signOutSupabaseSession();
    } finally {
      setTokenInput('');
      setBundle({ me: null, credits: null, payments: null, emailSettings: null, runs: null, emailDeliveries: null });
      setNotice('Supabase 会话已退出。');
      notifyAuthChange();
      setBusy(false);
    }
  };

  return (
    <div className="account-shell">
      <nav className="top-nav">
        <div className="nav-left">
          <h1 className="nav-brand">Academic Cluster</h1>
          <span className="nav-subtitle">Account & Billing</span>
        </div>
        <div className="nav-right">
          <button className="nav-btn" onClick={() => navigate('/')}>工作台</button>
          <button className="nav-btn" onClick={() => navigate('/manage')}>管理后台</button>
        </div>
      </nav>

      <main className="account-main">
        <section className="account-hero">
          <div>
            <p className="route-kicker">SaaS Control Plane</p>
            <h2>账户、额度与投递</h2>
            <p>这里管理登录会话、任务额度、支付记录和任务完成后的邮件投递设置。</p>
          </div>
          <div className="account-token-badge">
            <span>Session</span>
            <strong>{shortToken(token)}</strong>
          </div>
        </section>

        {(error || notice) && <div className={error ? 'account-alert account-alert-error' : 'account-alert'}>{error || notice}</div>}

        <section className="account-grid">
          <div className="account-card account-card-wide">
            <div className="account-card-head">
              <div>
                <p className="route-kicker">Authentication</p>
                <h3>登录方式</h3>
              </div>
              <div className="account-mode-switch">
                <button className={`nav-btn ${authMode === 'supabase' ? 'active' : ''}`} onClick={() => setAuthMode('supabase')}>Supabase</button>
                <button className={`nav-btn ${authMode === 'token' ? 'active' : ''}`} onClick={() => setAuthMode('token')}>Token</button>
              </div>
            </div>

            {authMode === 'supabase' ? (
              <>
                <div className="account-login-grid">
                  <div>
                    <label className="center-section-label" htmlFor="supabase-email">Supabase Email</label>
                    <input id="supabase-email" className="center-input" value={supabaseEmail} onChange={(event) => setSupabaseEmail(event.target.value)} placeholder="name@example.com" />
                  </div>
                  <div>
                    <label className="center-section-label" htmlFor="supabase-password">Supabase Password</label>
                    <input id="supabase-password" className="center-input" type="password" value={supabasePassword} onChange={(event) => setSupabasePassword(event.target.value)} placeholder="******" />
                  </div>
                </div>
                <div className="account-actions">
                  <button className="manage-primary" onClick={() => void handleSupabaseSignIn()} disabled={busy || !supabaseConfigured}>登录</button>
                  <button className="manage-secondary" onClick={() => void handleSupabaseSignUp()} disabled={busy || !supabaseConfigured}>注册</button>
                  <button className="manage-secondary" onClick={() => void handleSupabaseSignOut()} disabled={busy}>退出</button>
                </div>
                {!supabaseConfigured && <div className="account-hint">Supabase 前端变量未配置。</div>}
              </>
            ) : (
              <>
                <label className="center-section-label" htmlFor="account-token">Bearer JWT</label>
                <textarea
                  id="account-token"
                  className="account-token-input"
                  value={tokenInput}
                  onChange={(event) => setTokenInput(event.target.value)}
                  placeholder="粘贴 Supabase 或其他身份系统签发的 JWT"
                  rows={4}
                />
                <div className="account-actions">
                  <button className="manage-primary" onClick={() => void handleSaveToken()} disabled={loading || !canLoadAccount}>保存并加载账户</button>
                  <button className="manage-secondary" onClick={handleClearToken}>清除会话</button>
                </div>
              </>
            )}
          </div>

          <div className="account-card account-summary-card">
            <p className="route-kicker">Profile</p>
            <h3>{String(user.email || user.id || '未登录')}</h3>
            <div className="account-stat-list">
              <div><span>角色</span><strong>{String(user.role || '-')}</strong></div>
              <div><span>状态</span><strong>{String(user.status || '-')}</strong></div>
              <div><span>Credits</span><strong>{formatNumber(credits.balanceCredits || credits.balance || 0)}</strong></div>
            </div>
            <button className="manage-secondary" onClick={() => void loadAccount()} disabled={loading || !canLoadAccount}>刷新账户</button>
          </div>

          <div className="account-card">
            <p className="route-kicker">Billing</p>
            <h3>购买额度</h3>
            <input className="center-year-input" value={checkoutCredits} onChange={(event) => setCheckoutCredits(event.target.value)} inputMode="numeric" />
            <div className="account-actions">
              <button className="manage-primary" onClick={() => void handleCheckout()} disabled={busy || !canLoadAccount}>创建 Checkout</button>
              <button className="manage-secondary" onClick={() => void handleBillingPortal()} disabled={busy || !canLoadAccount}>账单门户</button>
            </div>
          </div>

          <div className="account-card account-card-wide">
            <p className="route-kicker">Email Delivery</p>
            <h3>结果投递</h3>
            <label className="center-section-label" htmlFor="result-email">接收邮箱</label>
            <input id="result-email" className="center-input" value={emailDraft.resultEmail} onChange={(event) => setEmailDraft((current) => ({ ...current, resultEmail: event.target.value }))} placeholder="name@example.com" />
            <label className="account-check">
              <input type="checkbox" checked={emailDraft.sendResultEmail} onChange={(event) => setEmailDraft((current) => ({ ...current, sendResultEmail: event.target.checked }))} />
              <span>任务完成后发送 review.md 和 references.bib 下载链接</span>
            </label>
            <label className="center-section-label" htmlFor="artifact-ttl">临时文件保留小时</label>
            <input id="artifact-ttl" className="center-year-input" value={emailDraft.defaultArtifactTtlHours} onChange={(event) => setEmailDraft((current) => ({ ...current, defaultArtifactTtlHours: Number(event.target.value || 24) }))} inputMode="numeric" />
            <button className="manage-primary" onClick={() => void handleSaveEmail()} disabled={busy || !canLoadAccount}>保存邮件设置</button>
          </div>

          <div className="account-card account-card-wide">
            <p className="route-kicker">Runs</p>
            <h3>最近任务</h3>
            <div className="account-table-list">
              {runs.length > 0 ? runs.map((run) => (
                <article key={String(run.runId || run.id)}>
                  <strong>{String(run.topic || run.title || run.runId || run.id)}</strong>
                  <span>{String(run.status || '-')} · {String(run.current_step || '-')} · {formatTime(run.created_at || run.createdAt)}</span>
                </article>
              )) : <p className="account-empty">暂无任务记录。</p>}
            </div>
          </div>

          <div className="account-card account-card-wide">
            <p className="route-kicker">Ledger</p>
            <h3>额度流水</h3>
            <div className="account-table-list">
              {ledger.length > 0 ? ledger.map((item, index) => (
                <article key={`${item.id || index}-ledger`}>
                  <strong>{formatNumber(item.amountCredits || item.amount_credits || item.amount || 0)} credits</strong>
                  <span>{String(item.reason || item.type || '-')} · {formatTime(item.created_at || item.createdAt)}</span>
                </article>
              )) : <p className="account-empty">暂无额度流水。</p>}
            </div>
          </div>

          <div className="account-card account-card-wide">
            <p className="route-kicker">Payments</p>
            <h3>支付记录</h3>
            <div className="account-table-list">
              {payments.length > 0 ? payments.map((item, index) => (
                <article key={`${item.id || index}-payment`}>
                  <strong>{String(item.status || '-')}</strong>
                  <span>{formatNumber(item.credits || item.amountCredits || 0)} credits · {formatTime(item.created_at || item.createdAt)}</span>
                </article>
              )) : <p className="account-empty">暂无支付记录。</p>}
            </div>
          </div>

          <div className="account-card account-card-wide">
            <p className="route-kicker">Email Logs</p>
            <h3>邮件投递记录</h3>
            <div className="account-table-list">
              {deliveries.length > 0 ? deliveries.map((item, index) => (
                <article key={`${item.id || index}-delivery`}>
                  <strong>{String(item.status || '-')}</strong>
                  <span>{String(item.toEmail || item.to_email || '-')} · {formatTime(item.created_at || item.createdAt)}</span>
                </article>
              )) : <p className="account-empty">暂无邮件投递记录。</p>}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
